// app/src/lib/scale/rbac.js — role-based access control matrix (US-5.3).
//
// Roles & permissions are enforced both client-side (this module) AND server-side
// by API Gateway authorizers reading the `custom:role` Cognito claim. The client
// matrix is the SAME source of truth — surface it via `can()` everywhere a UI
// action might be gated.
//
// IMPORTANT: never use these checks as the only enforcement for actions that
// touch shared/server state. Always treat the server as authoritative; this
// module is for UX (hiding buttons, showing read-only states).

/** @typedef {'owner'|'manager'|'bookkeeper'|'accountant'|'staff'} Role */

/**
 * Capabilities the matrix is checked against. Keep this list small and stable —
 * adding a new permission means auditing every Role row below.
 *
 * @typedef {(
 *   'read.financial' |
 *   'invoice.create' | 'invoice.edit' | 'invoice.approve' | 'invoice.categorize' |
 *   'po.create' | 'po.edit' | 'po.submit' | 'po.approve' |
 *   'vendor.edit' | 'item.edit' | 'recipe.edit' |
 *   'salesEntry.write' | 'tax.write' | 'tax.export' |
 *   'export.csv' |
 *   'user.invite' | 'user.manage' | 'billing.manage' |
 *   'integration.manage' | 'webhook.manage' |
 *   'audit.view' | 'audit.export' |
 *   'scan.create'
 * )} Capability
 */

const MATRIX = Object.freeze({
  owner: new Set([
    'read.financial',
    'invoice.create','invoice.edit','invoice.approve','invoice.categorize',
    'po.create','po.edit','po.submit','po.approve',
    'vendor.edit','item.edit','recipe.edit',
    'salesEntry.write','tax.write','tax.export',
    'export.csv',
    'user.invite','user.manage','billing.manage',
    'integration.manage','webhook.manage',
    'audit.view','audit.export',
    'scan.create',
  ]),
  manager: new Set([
    'read.financial',
    'invoice.create','invoice.edit','invoice.approve','invoice.categorize',
    'po.create','po.edit','po.submit','po.approve',
    'vendor.edit','item.edit','recipe.edit',
    'salesEntry.write','tax.write','tax.export',
    'export.csv',
    'scan.create',
  ]),
  bookkeeper: new Set([
    // Per §11: Bookkeeper can edit invoices, enter sales, view price book.
    // Cannot place / approve POs and cannot manage vendors/items.
    'read.financial',
    'invoice.create','invoice.edit','invoice.categorize',
    'salesEntry.write',
    'tax.write','tax.export',
    'export.csv',
    'scan.create',
  ]),
  accountant: new Set([
    // Time-bounded read-only. Caller MUST also check expiry via members.isMemberActive().
    'read.financial', 'export.csv', 'tax.export',
  ]),
  staff: new Set([
    // Staff sees Today + scan only; explicitly no financial figures.
    'scan.create',
  ]),
});

const VALID_ROLES = new Set(Object.keys(MATRIX));

export function isValidRole(role) {
  return typeof role === 'string' && VALID_ROLES.has(role);
}

/** @param {Role} role @param {Capability} capability */
export function can(role, capability) {
  if (!isValidRole(role)) return false;
  return MATRIX[role].has(capability);
}

/** Throws with code='FORBIDDEN' on denial. */
export function require_(role, capability) {
  if (!can(role, capability)) {
    const err = new Error(`Forbidden: role "${role}" lacks "${capability}"`);
    err.code = 'FORBIDDEN';
    throw err;
  }
}

export const ROLES = Object.freeze([...VALID_ROLES]);
